/**
 * DI-3-F — ONE tab-wide upload queue (story §2.1 `queue.ts`; locked
 * one-global-concurrency-cap-across-sets).
 *
 * Every PUT of every set (Put Block, Put Block List, Put Blob) runs through
 * `run(key, fn)`. At most `UPLOAD_CONCURRENCY` of them are in flight for the
 * whole tab — the uplink is the long pole (Intake Workflows v2 §6: 6.3 GB ≈
 * 42 min at 20 Mbit/s), and four 1 MiB requests keep a hotel uplink busy
 * without starving the metadata round trip of the NEXT set. A free slot goes
 * to the waiting PUT with the SMALLEST key — `[set order, file order, block]`
 * — so the sets are served in Start order ("queued · after set 1"), a set's
 * files in their order, and a file's commit never waits behind a later file.
 *
 * "Pause uploads" stops granting slots (the PUTs in flight finish — an
 * aborted block would only be sent again); "Resume uploads" continues.
 * DI-3-S68 B3 ("rows before bytes") — a HOLD does the same without the
 * pilot: while the runner sends a set's rows or its `metadata-complete`, its
 * driver holds the queue (`hold(holder, true)`), no new PUT starts, the ones
 * in flight finish; the last holder letting go lets it drain again. A hold is
 * not a pause: the banner's "Pause uploads" state never shows it. The
 * cap (`UPLOAD_CONCURRENCY`) is unchanged. DI-3-S68 F13 — measured on 3G
 * the in-flight blocks (up to 4 × 1 MiB, 20–30 s each) still doubled the
 * rows' time: a hold now ABORTS the abortable PUTs in flight (Put Block /
 * Put Blob — replayed with the same block id, idempotent on Azure); the
 * commit (Put Block List) is never aborted.
 *
 * DI-3-S68 F12 — the measured rate is the bytes of the PUTs that finished in
 * the LAST `RATE_WINDOW_MS` over that window (a sliding window, no lifetime
 * average: an earlier fast upload in the same tab no longer props up the
 * number on a slow line). `rate()` is null — "measuring…" — until the window
 * has run `RATE_MIN_MS` and holds a finished PUT; the measurement starts
 * over when the queue sat idle for `RATE_IDLE_RESET_MS` (a pause, the gap
 * between two drops) and when the connection changes (`online` /
 * `offline`). The time left is computed from this rate only.
 */

/** DI-3-S68 F12 — the rate's sliding window. */
export const RATE_WINDOW_MS = 45_000;
/** DI-3-S68 F12 — "measuring…" until the window ran this long. */
export const RATE_MIN_MS = 5_000;
/** DI-3-S68 F12 — idle this long, the next PUT starts a new measurement. */
export const RATE_IDLE_RESET_MS = 5_000;

/**
 * PUTs in flight for the whole tab (a shape choice for Christian, story §5).
 * Four 1 MiB blocks ≈ 4 × 0.4 s on a 20 Mbit/s uplink: enough in flight to
 * hide the request latency, few enough that a pause takes effect at once.
 */
export const UPLOAD_CONCURRENCY = 4;

export type QueueKey = readonly number[];

function compare(a: QueueKey, b: QueueKey): number {
  const n = Math.max(a.length, b.length);
  for (let i = 0; i < n; i++) {
    const d = (a[i] ?? 0) - (b[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

interface Waiter {
  key: QueueKey;
  seq: number;
  grant: () => void;
}

export interface QueueSnapshot {
  inFlight: number;
  waiting: number;
  paused: boolean;
  /** DI-3-S68 B3 — rows go first: no new slot is granted meanwhile. */
  held: boolean;
  /** Bytes of the PUTs that finished (committed or not). */
  bytesDone: number;
}

export class UploadQueue {
  private active = 0;
  private waiters: Waiter[] = [];
  private seq = 0;
  private pausedValue = false;
  /** DI-3-S68 B3 — who holds the queue (each driver, by its own key). */
  private readonly holders = new Set<string>();
  private listeners = new Set<(s: QueueSnapshot) => void>();
  private bytes = 0;
  /** DI-3-S68 F12 — the finished PUTs of the window: when, how many bytes. */
  private samples: { t: number; n: number }[] = [];
  /** DI-3-S68 F12 — when the current measurement began (null: none yet). */
  private measureFrom: number | null = null;
  /** DI-3-S68 F12 — when the queue last went idle (nothing in flight). */
  private idleSince: number | null = null;
  /** Most PUTs ever in flight at once — tests read it. */
  maxInFlightSeen = 0;
  /**
   * DI-3-S68 F13 — the abortable PUTs in flight: a hold aborts them (Put
   * Block / Put Blob are replayed — deterministic block ids, idempotent).
   */
  private readonly abortable = new Set<AbortController>();
  /** DI-3-S68 F13 — PUTs a hold aborted so far (tests read it). */
  abortedByHold = 0;

  constructor(
    readonly concurrency: number = UPLOAD_CONCURRENCY,
    private readonly now: () => number = () => Date.now(),
    win: Pick<Window, "addEventListener"> | null = typeof window !== "undefined"
      ? window
      : null,
  ) {
    // DI-3-S68 F12 — a new connection is a new line: measure again
    win?.addEventListener("online", () => {
      this.resetRate();
    });
    win?.addEventListener("offline", () => {
      this.resetRate();
    });
  }

  get paused(): boolean {
    return this.pausedValue;
  }

  /** DI-3-S68 B3 — is any holder holding the queue? */
  get held(): boolean {
    return this.holders.size > 0;
  }

  /**
   * DI-3-S68 B3 — `holder` holds (`on`) or lets go of the queue. While any
   * holder holds, no new slot is granted (the PUTs in flight finish).
   */
  hold(holder: string, on: boolean): void {
    const was = this.held;
    if (on) this.holders.add(holder);
    else this.holders.delete(holder);
    if (was === this.held) return;
    if (!this.held) this.scheduleDrain();
    else {
      // DI-3-S68 F13 — rows first, really: the block PUTs in flight would
      // keep sharing a slow line with the rows; abort them (they ask for a
      // slot again and wait for the hold to end — never a failed attempt)
      for (const c of this.abortable) {
        this.abortedByHold += 1;
        c.abort();
      }
      this.abortable.clear();
    }
    this.emit();
  }

  snapshot(): QueueSnapshot {
    return {
      inFlight: this.active,
      waiting: this.waiters.length,
      paused: this.pausedValue,
      held: this.held,
      bytesDone: this.bytes,
    };
  }

  subscribe(fn: (s: QueueSnapshot) => void): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /**
   * Run `fn` when a slot is free and `key` is the smallest waiting key.
   * DI-3-S68 F13 — `abortable`: `fn` gets a signal a hold aborts (the
   * caller replays the request; an aborted one is no failed attempt).
   */
  async run<T>(
    key: QueueKey,
    fn: (signal?: AbortSignal) => Promise<T>,
    opts: { abortable?: boolean } = {},
  ): Promise<T> {
    await new Promise<void>((grant) => {
      this.waiters.push({ key, seq: this.seq++, grant });
      this.scheduleDrain();
    });
    const ctl = opts.abortable ? new AbortController() : null;
    if (ctl) this.abortable.add(ctl);
    try {
      return await fn(ctl?.signal);
    } finally {
      if (ctl) this.abortable.delete(ctl);
      this.active -= 1;
      if (this.active === 0) this.idleSince = this.now();
      this.scheduleDrain();
      this.emit();
    }
  }

  /** Count `n` bytes as sent (a PUT finished). */
  addBytes(n: number): void {
    this.bytes += n;
    const t = this.now();
    this.measureFrom ??= t;
    this.samples.push({ t, n });
    this.prune(t);
    this.emit();
  }

  /** DI-3-S68 F12 — forget the measurement ("measuring…" again). */
  resetRate(): void {
    this.samples = [];
    this.measureFrom = this.active > 0 ? this.now() : null;
    this.emit();
  }

  private prune(t: number): void {
    const from = t - RATE_WINDOW_MS;
    let k = 0;
    while (k < this.samples.length && (this.samples[k]?.t ?? 0) < from) k++;
    if (k > 0) this.samples.splice(0, k);
  }

  pause(): void {
    if (this.pausedValue) return;
    this.pausedValue = true;
    this.emit();
  }

  resume(): void {
    if (!this.pausedValue) return;
    this.pausedValue = false;
    this.scheduleDrain();
    this.emit();
  }

  /**
   * DI-3-S68 F12 — bytes per second over the sliding window; null while
   * measuring (the window ran less than `RATE_MIN_MS`, or no PUT finished
   * in it).
   */
  rate(): number | null {
    if (this.measureFrom === null) return null;
    const t = this.now();
    this.prune(t);
    const span = t - Math.max(this.measureFrom, t - RATE_WINDOW_MS);
    if (span < RATE_MIN_MS || this.samples.length === 0) return null;
    let sum = 0;
    for (const x of this.samples) sum += x.n;
    return sum / (span / 1000);
  }

  /**
   * Grant on a microtask, not at once: every PUT that asks in the same turn
   * (a pass starting its files, several sets resuming) is in the list before
   * the first slot is given, so the smallest key really goes first.
   */
  private drainScheduled = false;
  private scheduleDrain(): void {
    if (this.drainScheduled) return;
    this.drainScheduled = true;
    queueMicrotask(() => {
      this.drainScheduled = false;
      this.drain();
    });
  }

  private drain(): void {
    while (
      !this.pausedValue &&
      this.holders.size === 0 &&
      this.active < this.concurrency &&
      this.waiters.length > 0
    ) {
      this.waiters.sort((a, b) => compare(a.key, b.key) || a.seq - b.seq);
      const next = this.waiters.shift();
      if (!next) break;
      // DI-3-S68 F12 — the first PUT after a long idle starts a new measurement
      const t = this.now();
      if (
        this.active === 0 &&
        (this.measureFrom === null ||
          (this.idleSince !== null && t - this.idleSince >= RATE_IDLE_RESET_MS))
      ) {
        this.samples = [];
        this.measureFrom = t;
      }
      this.idleSince = null;
      this.active += 1;
      this.maxInFlightSeen = Math.max(this.maxInFlightSeen, this.active);
      next.grant();
    }
    this.emit();
  }

  private emit(): void {
    const s = this.snapshot();
    for (const l of this.listeners) l(s);
  }
}
